import { ComponentProps } from 'react';
import {
  Link,
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';

import { Select } from '@fly/core';
import { relativeRoutes, routes } from '@fly/shared';
import { BreadcrumbComponent } from '@fly/shared-ui';
import { Link as RadixLink, Skeleton } from '@radix-ui/themes';

import { useGetEnvironmentsQuery } from '../../store';

const tabRoutePatterns: Record<string, string> = {
  [relativeRoutes.RUNTIME_ENVIRONMENTS_DEPLOYMENTS]:
    routes.DASHBOARD.RUNTIME_ENVIRONMENTS.ENVIRONMENT.DEPLOYMENTS,
  [relativeRoutes.RUNTIME_ENVIRONMENTS_TOKEN]:
    routes.DASHBOARD.RUNTIME_ENVIRONMENTS.ENVIRONMENT.TOKEN,
};

export const EnvironmentBreadcrumb: BreadcrumbComponent = ({
  isLast,
}: ComponentProps<BreadcrumbComponent>) => {
  const { environmentId: routeParam } = useParams() as {
    environmentId: string;
  };
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { currentData: environments, isLoading } = useGetEnvironmentsQuery();

  const currentEnvironment = environments?.data?.find(
    (env) => env.id === routeParam
  );

  if (isLoading) {
    return <Skeleton className='h-[18px] w-[120px] rounded-fly-2' />;
  }

  const to = generatePath(
    routes.DASHBOARD.RUNTIME_ENVIRONMENTS.ENVIRONMENT.ROOT,
    { environmentId: currentEnvironment?.id ?? routeParam }
  );
  const title = currentEnvironment?.name ?? routeParam;

  const navigateToEnvironment = (environmentId: string) => {
    const currentTab = pathname.split('/').pop() || '';
    const routePattern =
      tabRoutePatterns[currentTab] ??
      routes.DASHBOARD.RUNTIME_ENVIRONMENTS.ENVIRONMENT.ROOT;

    const path = generatePath(routePattern, {
      environmentId,
    });
    navigate(path);
  };

  return isLast ? (
    <RadixLink className='text-fly-general-black'>
      {environments?.data && environments?.data?.length > 1 ? (
        <Select
          variant='ghost'
          color='neutral'
          size='1'
          className='text-size-fly-3'
          onValueChange={navigateToEnvironment}
          value={currentEnvironment?.id ?? ''}
          options={environments.data.map((environment) => ({
            value: environment.id,
            textValue: environment.name,
          }))}
        />
      ) : (
        <strong>{title}</strong>
      )}
    </RadixLink>
  ) : (
    <RadixLink asChild color='gray'>
      <Link className='font-semibold' to={to}>
        {title}
      </Link>
    </RadixLink>
  );
};
